/**
 * The table of contents in `snippets/pta-toc.liquid`: which section you are in, whether the card is a
 * list or a disclosure, and dropping any entry that points at nothing.
 *
 * Everything here is an enhancement. The list is real anchors rendered by Liquid and the `<details>`
 * ships `open`, so with this script blocked the page is a full table of contents above a readable body —
 * which is why the mobile collapse happens here rather than in the markup.
 *
 * ## Why a scroll listener and not IntersectionObserver
 *
 * "Which section am I in" is a question about the *last* heading that has passed the top of the
 * viewport, and that is not what an observer reports. An observer fires on headings entering and leaving
 * a band, so a section longer than the viewport has no heading intersecting anything and the highlight
 * falls off; a run of short sections fires several entries in one frame and the answer depends on
 * callback order. Reading positions directly at rAF cadence answers the actual question and costs one
 * `getBoundingClientRect` per heading per frame, on at most a couple of dozen headings.
 */
const DESKTOP = window.matchMedia('screen and (min-width: 990px)');

/* Matches `scroll-margin-block-start` on the headings, so the section considered current is the one
   whose heading has reached the point it would be parked at by a click. */
const HEADER_FALLBACK = 60;
const CLEARANCE = 24;

const toc = document.querySelector('.pta-toc');

if (toc) {
  const current = toc.querySelector('.pta-toc__summary-current');

  /* Entries whose target is not on the page are removed before anything else reads the list.
     `snippets/pta-toc.liquid` lets a caller append entries for sections it cannot see — the brand guide
     does, for its generated compatibility tables and its FAQ — and neither is guaranteed to render: a
     brand we stock nothing for has no table. A dead anchor in a numbered list is worse than a shorter
     list, so they go, and the remaining numbers are left alone rather than resequenced, because the
     numbers are also printed in the collapsed bar and a reshuffle on load would be visible. */
  const links = [];
  const targets = [];

  for (const link of toc.querySelectorAll('.pta-toc__link')) {
    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));

    if (target) {
      links.push(link);
      targets.push(target);
    } else {
      link.closest('.pta-toc__item')?.setAttribute('hidden', '');
    }
  }

  /* The second sticky bar.
   *
   * `sections/pta-finder-cta.liquid` is inside `sections/header-group.json`, after the header, and sticks
   * itself at `--header-height`. So the chrome parked at the top of the viewport is the header *plus*
   * that bar, and a card sticking at `--header-height` alone slides its own top underneath it — which is
   * what it did: the "On this page" heading and the first entry's top edge were cut off.
   *
   * Horizon's `--header-group-height` is not the number. It sums every child of the group except the
   * header, which here means the announcement bar as well, and that one scrolls away. So it is measured
   * directly, and published as a variable both the sticky `top` and the anchors' `scroll-margin` read —
   * the FAQ target sits outside the layout element, so it goes on `body` where Horizon puts its own.
   */
  const stickyBar = document.querySelector('.pta-finder-cta')?.closest('.shopify-section') ?? null;

  /* The page does not scroll the window on desktop.
   *
   * `assets/base.css` gives `html` and `body` `height: 100dvh; overflow: hidden` at 990px and up, and
   * makes `.page-wrapper` the scroll container — `height: 100dvh; overflow-y: auto`. So above that width
   * `window.scrollY` is permanently 0, `document.documentElement.scrollHeight` equals the viewport, and a
   * `scroll` listener on `window` never fires, because the scrolling happens on an element.
   *
   * Both of the reported bugs were this. The bottom-of-page test compared a scroll position that never
   * moved against a document height that equalled the viewport, so it read as "at the bottom" forever and
   * the card opened on its last entry — the FAQ — on a page nobody had scrolled. Guarding that test then
   * pinned it to the first entry instead, for the same underlying reason: nothing was ever re-measured.
   *
   * Below 990px the media query does not apply and the document scrolls normally, so both listeners are
   * attached and whichever element is really scrolling is the one read.
   */
  const wrapper = document.querySelector('.page-wrapper');

  const scroller = () => {
    if (wrapper && wrapper.scrollHeight - wrapper.clientHeight > 1) return wrapper;
    return document.scrollingElement ?? document.documentElement;
  };

  const publishChrome = () => {
    const height = stickyBar?.offsetHeight ?? 0;
    document.body.style.setProperty('--pta-toc-chrome', `${height}px`);
    return height;
  };

  const headerOffset = () => {
    const declared = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'));
    return (Number.isFinite(declared) ? declared : HEADER_FALLBACK) + publishChrome() + CLEARANCE;
  };

  let active = -1;

  const paint = (index) => {
    if (index === active) return;
    active = index;

    links.forEach((link, i) => {
      if (i === index) {
        link.setAttribute('aria-current', 'location');
      } else {
        link.removeAttribute('aria-current');
      }
    });

    /* The link's own text, so the collapsed bar reads exactly as the row does — "3. Charging" — with the
       number included and without a second copy of the label in a data attribute. */
    if (current) {
      current.textContent = links[index]?.textContent.replace(/\s+/g, ' ').trim() ?? '';
    }
  };

  const measure = () => {
    const offset = headerOffset();
    let index = 0;

    for (let i = 0; i < targets.length; i++) {
      if (targets[i].getBoundingClientRect().top <= offset) index = i;
    }

    /* The final section is often too short to ever push its heading past the offset line — the page ends
       first — so without this it could never become current however far you scrolled.

       Decided on geometry rather than on how much scrolling is left, because the arithmetic is not
       trustworthy here. Measured on the mobile layout of a brand guide: the scroller reports
       `scrollHeight - clientHeight` of 9157 while the furthest it will actually travel is 8561, so
       "distance remaining" never reaches zero and a test written that way simply never fires. The desktop
       container disagrees by nothing, which is exactly the kind of difference that ships looking fine.

       Asking whether the last heading has climbed into the top half of the viewport needs no scroll
       position at all, so it behaves the same whichever element is doing the scrolling. It also cannot
       contradict the loop above: if that heading has passed the offset line the loop has already chosen
       it. */
    const last = targets[targets.length - 1];
    if (last && last.getBoundingClientRect().top < window.innerHeight * 0.5) index = links.length - 1;

    paint(index);
  };

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      measure();
    });
  };

  /* Desktop has no visible summary, so an `open` left false there would hide the card's contents with no
     way to reopen them. Re-applied on every breakpoint change rather than only at load. */
  const syncOpen = () => {
    toc.open = DESKTOP.matches;
  };

  syncOpen();
  DESKTOP.addEventListener('change', syncOpen);

  /* Collapse after choosing a destination on mobile: leaving the list open would park a full-height card
     over the section it just scrolled to. The jump itself is the browser's own, so the anchor keeps
     working with the keyboard, with a middle-click and in the address bar. */
  toc.addEventListener('click', (event) => {
    if (DESKTOP.matches) return;
    if (!event.target.closest('.pta-toc__link')) return;
    toc.open = false;
  });

  if (links.length > 0) {
    /* Both, deliberately. `.page-wrapper` is the scroller at 990px and up and the window is the scroller
       below it, and the breakpoint can be crossed by resizing without a reload. Listening to the one that
       is not currently scrolling costs nothing. */
    window.addEventListener('scroll', schedule, { passive: true });
    wrapper?.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });

    /* Every heading position this reads is a measurement of a page that is still settling. Images arrive,
       the web font swaps, the sticky bar rewraps — each moves every heading below it, and none of them
       fires `scroll` or `resize`. `sections/header.liquid` needed a `ResizeObserver` for the same class of
       bug and `assets/header-menu.js` carries the note about it: a late layout change has nothing to
       observe unless you observe it.

       `load` and the observer overlap deliberately. `load` is the one that corrects the first paint, and
       the observer catches everything after it. */
    window.addEventListener('load', schedule);
    new ResizeObserver(schedule).observe(document.body);

    publishChrome();
    measure();
  }
}
